import { Component, ElementRef, ViewChild } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { EnaioAccessType } from 'src/modules/enaio/shared/EnaioAccessType';
import { EnaioCallGetUserGroupList } from 'src/modules/enaio/shared/EnaioCallGetUserGroupList';
import { EnaioCallGetUserInfo } from 'src/modules/enaio/shared/EnaioCallGetUserInfo';
import { EnaioCallGetUserList } from 'src/modules/enaio/shared/EnaioCallGetUserList';
import { EnaioCallGetUserPermissions } from 'src/modules/enaio/shared/EnaioCallGetUserPermissions';
import { EnaioCallGetUserPrefs } from 'src/modules/enaio/shared/EnaioCallGetUserPrefs';
import { EnaioCheckPermissionAccessTypeResult } from 'src/modules/enaio/shared/EnaioCheckPermissionAccessTypeResult';
import { EnaioCheckPermissionResult } from 'src/modules/enaio/shared/EnaioCheckPermissionResult';
import { EnaioObjectIdAndType } from 'src/modules/enaio/shared/EnaioObjectIdAndType';
import { EnaioObjectTypeId } from 'src/modules/enaio/shared/EnaioObjectTypeId';
import { EnaioUser } from 'src/modules/enaio/shared/EnaioUser';
import { EnaioUserGroup } from 'src/modules/enaio/shared/EnaioUserGroup';
import { EnaioUserIdentification } from 'src/modules/enaio/shared/EnaioUserIdentification';
import { EnaioUserPrefs } from 'src/modules/enaio/shared/EnaioUserPrefs';
import { FrontendFieldListItem } from 'src/modules/sm-base/models/frontend-field-list-item.model';
import { TableCellType } from 'src/modules/sm-base/models/table-cell-type.enum';
import { TableCell } from 'src/modules/sm-base/models/table-cell.model';
import { TableColumn } from 'src/modules/sm-base/models/table-column.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { OrdinaryObject } from 'src/modules/utils/shared/ordinary-object.model';
import { Utils } from 'src/modules/utils/shared/utils';
import { DhTools } from '../../models/dh-tools.model';
import { GuiUtils } from 'src/modules/utils/misc/gui-utils';
import { EnaioCallGetUserPassword } from 'src/modules/enaio/shared/EnaioCallGetUserPassword';
import { FrontendFieldDefinition } from 'src/modules/sm-base/models/frontend-field-definition.model';
import { FrontendFieldType } from 'src/modules/sm-base/models/frontend-field-type.enum';
import { EnaioCallUpdateUser } from 'src/modules/enaio/shared/EnaioCallUpdateUser';

@Component({
  selector: 'app-enaio-users',
  templateUrl: './enaio-user.component.html',
  styleUrls: ['./enaio-user.component.scss']
})
export class EnaioUserComponent extends ComponentView {

    users: EnaioUser[];
    userGroups: EnaioUserGroup[];
    objectTypes: EnaioObjectTypeId[];
    compareToUsersGui: FrontendFieldListItem[] = [];
    archivesGui: FrontendFieldListItem[] = [];
    prefs: EnaioUserPrefs;
    userTable: TableData;
    prefsTable: TableData;
    selectedUser: string;
    selectedCompareToUser: string;
    selectedArchive: EnaioObjectTypeId;
    devMode = false;
    pw: string[] = ["", ""];

    @ViewChild("main", {read: ElementRef}) private mainRef: ElementRef;

    async initParams(): Promise<boolean> {
        this.addKeyboardListener("Ctrl-S", event => {
            if (GuiUtils.isComponentVisible(this.mainRef)) {
                this.devMode = true;
                Utils.setTimerOnce(60_000, () => {
                    this.devMode = false;
                });
                event.preventDefault();
            }
        });

        this.users = Utils.arraySortBy(await DhTools.enaioCall(Utils.fromPlain(EnaioCallGetUserList, {})), u => u.userName);
        this.objectTypes = Utils.arraySortBy(await DhTools.backendCall("api/enaio/getObjectTypes").list(EnaioObjectTypeId), ot => ot.archive.name + " - " + ot.objectType.name);
        this.userGroups = await DhTools.enaioCall(Utils.fromPlain(EnaioCallGetUserGroupList, {}));

        this.archivesGui = this.objectTypes.filter(ot => ot.archive.internalName == ot.objectType.internalName).map(ot => new FrontendFieldListItem(ot, ot.archive.name));
        this.compareToUsersGui = [new FrontendFieldListItem(null, ""), ...this.users.map(m => new FrontendFieldListItem(m.userName, m.userName))];

        return true;
    }

    async exportUsersToXlsx(): Promise<void> {
        let fileName = await DhTools.saveDialog("EnaioUsers.xlsx");
        if (!Utils.isNoe(fileName)) {
            await DhTools.backendCall("api/enaio/exportUsersToXlsx", { fileName }).getText();
        }
    }

    async selectUser(): Promise<void> {
        await this.run();
    }

    async getPermissions(userName: string, objectTypes: EnaioObjectTypeId[]): Promise<EnaioCheckPermissionResult[]> {
        if (Utils.isNoe(userName)) {
            return null;
        }

        let user = await DhTools.enaioCall<EnaioUser>(Utils.fromPlain(EnaioCallGetUserInfo, {
            userId: EnaioUserIdentification.byUserName(userName),
            returnGroups: true
        }));
        let call = Utils.fromPlain(EnaioCallGetUserPermissions, {
            user: EnaioUserIdentification.byUserName(userName),
            detailed: true
        });
        let result = await DhTools.enaioCall<EnaioCheckPermissionResult[]>(call);

        if (user != null) {
            for (let objectType of objectTypes) {
                for (let group of user.userGroups) {
                    if (result.find(p => p.fromUserGroup == group.id && p.forObject.typeId == objectType.objectType.id) == null) {
                        result.push(Utils.fromPlain(EnaioCheckPermissionResult, {
                            fromUserGroup: group.id,
                            forObject: new EnaioObjectIdAndType(0, objectType.objectType.id),
                            accessTypes: Utils.getEnumValues(EnaioAccessType).map(type => Utils.fromPlain(EnaioCheckPermissionAccessTypeResult, {
                                accessType: type,
                                allow: false
                            }))
                        }));
                    }
                }
            }
        }

        return result;
    }

    async run(): Promise<void> {
        if (this.selectedArchive == null) {
            this.userTable = null;
            this.prefsTable = null;
            return;
        }
        let objectTypes = this.objectTypes.filter(ot => ot.archive.internalName == this.selectedArchive.archive.internalName);
        let permissions = await this.getPermissions(this.selectedUser, objectTypes);
        let permissionsCompare = this.selectedCompareToUser == null ? null : await this.getPermissions(this.selectedCompareToUser, objectTypes);

        let userGroups = this.userGroups.filter(ug => permissions.some(p => p.fromUserGroup == ug.id) || permissionsCompare?.some(p => p.fromUserGroup == ug.id));

        this.userTable = new TableData([
            new TableColumn("group", "Gruppe", TableCellType.text, { autoWidth: 15, sortable: false }),
            new TableColumn("delete", "Objekt löschen", TableCellType.text, { autoWidth: 15, sortable: false }),
            new TableColumn("readMetaData", "Indexdaten lesen", TableCellType.text, { autoWidth: 15, sortable: false }),
            new TableColumn("writeMetaData", "Indexdaten schreiben", TableCellType.text, { autoWidth: 15, sortable: false }),
            new TableColumn("update", "Objekt schreiben", TableCellType.text, { autoWidth: 15, sortable: false }),
            new TableColumn("openExec", "Objekt ausgeben", TableCellType.text, { autoWidth: 15, sortable: false })
        ], Utils.arrayExplode(objectTypes, ot => [new TableRow<any>("", ot.objectType.name, { group: new TableCell(ot.objectType.name, { colSpan: 6, styleClass: "xbg-blue-200" }) }), ...userGroups.map(ug => {
            let p1 = permissions.find(p => p.fromUserGroup == ug.id && p.forObject.typeId == ot.objectType.id);
            let p2 = permissionsCompare?.find(p => p.fromUserGroup == ug.id && p.forObject.typeId == ot.objectType.id);
            return new TableRow("", ug, {
                group: ug.name,
                delete: this.getUserRightCell2(p1, p2, permissionsCompare != null, EnaioAccessType.delete),
                readMetaData: this.getUserRightCell2(p1, p2, permissionsCompare != null, EnaioAccessType.readMetaData),
                writeMetaData: this.getUserRightCell2(p1, p2, permissionsCompare != null, EnaioAccessType.writeMetaData),
                update: this.getUserRightCell2(p1, p2, permissionsCompare != null, EnaioAccessType.update),
                openExec: this.getUserRightCell2(p1, p2, permissionsCompare != null, EnaioAccessType.openExec)
            } as OrdinaryObject);
        })]));

        this.prefs = await DhTools.enaioCall<EnaioUserPrefs>(Utils.fromPlain(EnaioCallGetUserPrefs, {
            user: EnaioUserIdentification.byUserName(this.selectedUser)
        }));

        this.prefsTable = new TableData([
            new TableColumn("key", "Schlüssel", TableCellType.text, { autoWidth: 50 }),
            new TableColumn("value", "Wert", TableCellType.text, { flex: 1 })
        ], Utils.getOwnPropertyNames(this.prefs.raw).map(key => new TableRow("", key, {
                key,
                value: this.prefs.raw[key]
            } as OrdinaryObject)));
    }

    private getUserRightCell2(p: EnaioCheckPermissionResult, pCompare: EnaioCheckPermissionResult, useCompare: boolean, type: EnaioAccessType): TableCell {
        if (useCompare) {
            let c1 = this.getUserRightCell(p, type);
            let c2 = this.getUserRightCell(pCompare, type);
            return c1.value == c2.value ? c1 : new TableCell(c1.value + " vs " + c2.value, { styleClass: "xbg-orange-400" });
        }
        else {
            return this.getUserRightCell(p, type);
        }
    }


    private getUserRightCell(p: EnaioCheckPermissionResult, type: EnaioAccessType): TableCell {
        if (p == null) {
            return new TableCell("---", { styleClass: "xbg-gray-400" });
        }
        let data = p?.accessTypes.find(t => t.accessType == type);
        return data?.allow ? !Utils.isNoe(data.readableClause) ? new TableCell(data.readableClause, { styleClass: "xbg-yellow-400" }) : !Utils.isNoe(data.clause) ? new TableCell(data.clause, { styleClass: "xbg-yellow-400" }) :
            new TableCell("Ja", { styleClass: "xbg-green-400" }) : new TableCell("Nein", { styleClass: "xbg-red-400" });
    }

    async getPw(): Promise<void> {
        this.pw = await DhTools.enaioCall<string[]>(Utils.fromPlain(EnaioCallGetUserPassword, {
            user: EnaioUserIdentification.byUserName(this.selectedUser)
        }));
    }

    async changePw(): Promise<void> {
        let pw = await this.app.messageDialog.input<string>(new FrontendFieldDefinition("", "Neues Passwort", FrontendFieldType.text, { mandatory: true }), "Passwort ändern");
        if (pw != null) {
            await DhTools.enaioCall<string[]>(Utils.fromPlain(EnaioCallUpdateUser, {
                userInfo: Utils.fromPlain(EnaioUser, {
                    userName: this.selectedUser,
                    password: pw
                }),
                upsertFields: ["password"]
            }));
        }
    }
}
